<template>
  <UPageSection
    :dir="dir"
    :title="title"
    :description="subtitle"
    :ui="{
      root: 'min-w-0 overflow-x-clip',
      container: 'w-full max-w-full min-w-0 !gap-0 py-10 sm:py-14 lg:grid-cols-1 lg:py-10',
      title: 'max-w-full text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl text-center',
      description: 'mx-auto mt-4 max-w-3xl text-lg text-gray-500 dark:text-gray-400 text-center',
    }"
  >
    <div class="min-w-0 w-full">
      <div
        v-if="isGrid && displayItems.length"
        class="block-services__search mx-auto mb-8 mt-10 flex w-full max-w-2xl flex-col gap-3 rounded-2xl border border-default bg-elevated/50 p-3 shadow-sm sm:mb-10 sm:mt-12 sm:flex-row sm:items-center sm:p-4"
        role="search"
      >
        <label class="relative min-w-0 flex-1">
          <span class="sr-only">{{ searchLabel }}</span>
          <UIcon
            name="i-lucide-search"
            class="pointer-events-none absolute inset-s-4 top-1/2 size-5 -translate-y-1/2 text-muted"
          />
          <input
            v-model="searchQuery"
            type="search"
            :dir="dir"
            :placeholder="searchPlaceholder"
            :aria-label="searchLabel"
            class="h-12 w-full rounded-xl border border-default bg-default ps-12 pe-4 text-sm text-default outline-none transition placeholder:text-muted focus:border-primary focus:ring-4 focus:ring-primary/10"
          >
        </label>
        <p
          class="shrink-0 px-2 text-center text-sm text-muted sm:text-start"
          aria-live="polite"
        >
          {{ resultCountLabel }}
        </p>
      </div>

      <!-- One card stays a single column wide, centered, instead of stretching the row. -->
      <div
        v-if="soloItem && isGrid"
        class="block-services__solo mx-auto mb-8 sm:mb-10"
      >
        <ServicePricingCard
          class="h-full"
          v-bind="cardProps(soloItem)"
          :builder-preview="builderPreview"
          :preview-navigate="previewNavigate || onNavigate"
          :on-navigate="onNavigate"
          :cta-handler="ctaHandlerFor(soloItem)"
          @cta="ctaHandlerFor(soloItem)?.()"
        />
      </div>

      <!-- Grid layout (services page) -->
      <div
        v-else-if="isGrid && filteredItems.length"
        class="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3 xl:gap-7"
      >
        <ServicePricingCard
          v-for="(item, index) in filteredItems"
          :key="item.id || index"
          v-bind="cardProps(item)"
          :builder-preview="builderPreview"
          :preview-navigate="previewNavigate || onNavigate"
          :on-navigate="onNavigate"
          :cta-handler="ctaHandlerFor(item)"
          @cta="ctaHandlerFor(item)?.()"
        />
      </div>

      <!-- Carousel layout (homepage) -->
      <ClientOnly v-else-if="!isGrid && displayItems.length">
        <div class="block-services__swiper my-8 min-w-0 w-full sm:my-12">
          <div
            v-if="showNavigation"
            class="mb-8 flex items-center gap-2"
            :class="dir === 'rtl' ? 'justify-end' : 'justify-start'"
          >
            <button
              type="button"
              class="block-services__nav-btn block-services__nav-prev border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navPrevLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'"
                class="size-4"
              />
            </button>
            <button
              type="button"
              class="block-services__nav-btn block-services__nav-next border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navNextLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-lucide-chevron-left' : 'i-lucide-chevron-right'"
                class="size-4"
              />
            </button>
          </div>

          <Swiper
            :modules="swiperModules"
            :dir="dir"
            :slides-per-view="slidesPerView"
            :space-between="spaceBetween"
            :breakpoints-base="'container'"
            :breakpoints="swiperBreakpoints"
            :navigation="navigationOptions"
            :pagination="paginationOptions"
            :autoplay="autoplayOptions"
            :loop="canLoop"
            class="equal-h-swiper block-services-swiper overflow-hidden"
          >
            <SwiperSlide
              v-for="(item, index) in displayItems"
              :key="item.id || index"
              class="h-auto py-2"
            >
              <ServicePricingCard
                class="h-full"
                v-bind="cardProps(item)"
                :builder-preview="builderPreview"
                :preview-navigate="previewNavigate || onNavigate"
                :on-navigate="onNavigate"
                :cta-handler="ctaHandlerFor(item)"
                @cta="ctaHandlerFor(item)?.()"
              />
            </SwiperSlide>
          </Swiper>
        </div>
        <template #fallback>
          <div class="grid grid-cols-2 items-stretch gap-3 sm:gap-5 lg:grid-cols-3">
            <ServicePricingCard
              v-for="(item, index) in displayItems"
              :key="item.id || index"
              v-bind="cardProps(item)"
            />
          </div>
        </template>
      </ClientOnly>

      <div
        v-else-if="catalogLoading"
        class="mx-auto my-10 grid w-full max-w-6xl grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 xl:grid-cols-3 xl:gap-7"
        aria-label="در حال بارگذاری خدمات"
        aria-busy="true"
      >
        <span
          v-for="index in 3"
          :key="index"
          class="h-72 animate-pulse rounded-2xl border border-default bg-elevated/50"
        />
      </div>
      <p
        v-else-if="isGrid && displayItems.length"
        class="mx-auto my-12 max-w-xl rounded-2xl border border-dashed border-default bg-elevated/30 px-6 py-10 text-center text-sm text-muted"
      >
        {{ noSearchResultsLabel }}
      </p>
      <p
        v-else
        class="my-12 text-center text-sm text-muted"
      >
        {{ emptyLabel }}
      </p>
    </div>

    <CampaignPackageCheckoutModal
      v-if="!builderPreview"
      :package-id="selectedPackageId"
      @update:package-id="selectedPackageId = $event"
    />
  </UPageSection>
</template>

<script setup lang="ts">
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import ServicePricingCard from '~/components/blocks/ServicePricingCard.vue'
import type { ServiceCardVariant } from '~/constants/serviceCardVariants'
import type { BookingServicePublic } from '~/types/booking'
import { formatRialFa } from '~/utils/money'
import { toFaDigits } from '~/utils/faDigits'

export type ServiceItem = {
  id?: string
  image?: string
  icon?: string
  title?: string
  description?: string
  price?: string
  features?: string[]
  ctaLabel?: string
  ctaLink?: string
  ctaIcon?: string
  kind?: 'service' | 'package' | 'page'
  packageId?: string
  requiresAppointment?: boolean
  pageSlug?: string
}

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    layout?: 'carousel' | 'grid'
    cardVariant?: ServiceCardVariant
    /** Live catalog: published service pages, session packages, or both. */
    source?: 'services' | 'packages' | 'both'
    items?: ServiceItem[]
    autoplay?: boolean
    autoplayDelay?: number
    loop?: boolean
    builderPreview?: boolean
    previewDevice?: 'desktop' | 'mobile'
    previewNavigate?: (to: string) => void
    onNavigate?: (to: string) => void
  }>(),
  {
    layout: 'carousel',
    cardVariant: 'solid',
    source: 'both',
    items: () => [],
    autoplay: true,
    autoplayDelay: 5000,
    loop: true,
  },
)

const isGrid = computed(() => props.layout === 'grid')
const swiperModules = [Pagination, Navigation, Autoplay]

const catalogSource = computed(() => {
  if (props.source === 'services' || props.source === 'packages' || props.source === 'both') {
    return props.source
  }
  return 'both'
})

const emptyLabel = computed(() => {
  const fa = pageLocale.value === 'fa'
  if (catalogSource.value === 'packages') {
    return fa ? 'پکیج فعالی برای نمایش نیست.' : 'No active packages to show.'
  }
  if (catalogSource.value === 'services') {
    return fa ? 'خدمت منتشرشده‌ای برای نمایش نیست.' : 'No published services to show.'
  }
  return fa
    ? 'خدمت یا پکیج منتشرشده‌ای برای نمایش نیست.'
    : 'No published services or packages to show.'
})

const searchQuery = ref('')
const searchLabel = computed(() => pageLocale.value === 'fa' ? 'جست‌وجو در خدمات' : 'Search services')
const searchPlaceholder = computed(() => pageLocale.value === 'fa'
  ? 'نام خدمت یا موضوع موردنظرتان را بنویسید…'
  : 'Search by service or topic…')
const noSearchResultsLabel = computed(() => pageLocale.value === 'fa'
  ? 'خدمتی با این عبارت پیدا نشد. عبارت دیگری را جست‌وجو کنید.'
  : 'No services match your search. Try another term.')
const resultCountLabel = computed(() => {
  const count = toFaDigits(String(filteredItems.value.length))
  return pageLocale.value === 'fa' ? `${count} نتیجه` : `${filteredItems.value.length} results`
})

const defaultCta = computed(() =>
  pageLocale.value === 'fa' ? 'دریافت مشاوره' : 'Get consultation',
)

const route = useRoute()
const packageCards = ref<ServiceItem[]>([])
const packageLoading = ref(true)
const selectedPackageId = ref<string | null>(null)
const catalogLoading = computed(() => catalogSource.value !== 'services' && packageLoading.value)

const requestFetch = useRequestFetch()
const { data: catalogData, error: catalogError } = await useAsyncData(
  'service-page-catalog',
  () => requestFetch<{ items: Array<{
    id: string
    slug: string
    kind: string
    requiresAppointment: boolean
    title: string
    excerpt: string
    coverUrl: string
    icon: string
    priceLabel: string
    ctaLabel: string
    features: string[]
  }> }>('/api/services'),
)

if (catalogError.value) throw catalogError.value

const catalogItems = computed<ServiceItem[]>(() => {
  const fa = pageLocale.value === 'fa'
  return (catalogData.value?.items ?? [])
    .filter((row) => row.slug && row.title)
    .map((row) => ({
      id: row.id,
      kind: row.kind === 'group' ? 'page' as const : 'page' as const,
      title: row.title,
      description: row.excerpt,
      image: row.coverUrl || undefined,
      price: row.priceLabel || undefined,
      features: row.features ?? [],
      ctaLabel: row.ctaLabel || (fa ? 'مشاهده اطلاعات بیشتر' : 'View service'),
      ctaLink: `/services/${encodeURIComponent(row.slug)}`,
      ctaIcon: fa ? 'i-lucide-arrow-up-left' : 'i-lucide-arrow-up-right',
      requiresAppointment: row.requiresAppointment,
      pageSlug: row.slug,
    }))
})

const displayItems = computed(() => {
  const services = catalogSource.value === 'packages' ? [] : catalogItems.value
  const packages = catalogSource.value === 'services' ? [] : packageCards.value
  return [...services, ...packages]
})

function normalizeSearchText(value: string): string {
  return value
    .replace(/[\u200c\u200f\u202a-\u202e]/g, '')
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase()
}

const filteredItems = computed(() => {
  const query = normalizeSearchText(searchQuery.value)
  if (!isGrid.value || !query) return displayItems.value
  return displayItems.value.filter((item) => {
    const searchable = [
      item.title,
      item.description,
      ...(item.features ?? []),
    ].filter(Boolean).join(' ')
    return normalizeSearchText(searchable).includes(query)
  })
})

const soloItem = computed(() => filteredItems.value.length === 1 ? filteredItems.value[0] : undefined)

function applyPackageQuery() {
  if (props.builderPreview) return
  const raw = route.query.package
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value)) {
    selectedPackageId.value = value
  }
}

const itemCount = computed(() => displayItems.value.length)

const spaceBetween = computed(() => {
  if (props.previewDevice === 'mobile') return 16
  if (props.previewDevice === 'desktop') return 24
  return 24
})

const slidesPerView = computed(() => {
  if (props.previewDevice === 'mobile') return 1
  if (props.previewDevice === 'desktop') return Math.min(2, Math.max(1, itemCount.value))
  return 1
})

const swiperBreakpoints = computed(() => {
  const max = Math.max(1, itemCount.value)
  return {
    0: { slidesPerView: Math.min(2, max), spaceBetween: 10 },
    360: { slidesPerView: Math.min(2.2, max), spaceBetween: 12 },
    480: { slidesPerView: Math.min(2.5, max), spaceBetween: 14 },
    600: { slidesPerView: Math.min(2.8, max), spaceBetween: 16 },
    768: { slidesPerView: Math.min(3, max), spaceBetween: 20 },
    1200: { slidesPerView: Math.min(3, max), spaceBetween: 24 },
  }
})

const showNavigation = computed(() => {
  if (props.previewDevice === 'mobile') return itemCount.value > 1
  if (props.previewDevice === 'desktop') return itemCount.value > 2
  return itemCount.value > 1
})

const navigationOptions = computed(() => {
  if (!showNavigation.value) return false
  return {
    prevEl: '.block-services__nav-prev',
    nextEl: '.block-services__nav-next',
  }
})

const navPrevLabel = computed(() =>
  pageLocale.value === 'fa' ? 'اسلاید قبلی' : 'Previous slide',
)

const navNextLabel = computed(() =>
  pageLocale.value === 'fa' ? 'اسلاید بعدی' : 'Next slide',
)

const paginationOptions = computed(() => ({
  clickable: true,
  dynamicBullets: true,
}))

const autoplayDelay = computed(() => {
  const value = Number(props.autoplayDelay)
  if (!Number.isFinite(value) || value < 1000) return 5000
  return Math.min(60000, Math.round(value))
})

const canLoop = computed(() => Boolean(props.loop) && itemCount.value > 1)

const autoplayOptions = computed(() => {
  if (!props.autoplay || itemCount.value < 2) return false
  return {
    delay: autoplayDelay.value,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
    stopOnLastSlide: !props.loop,
  }
})

function resolveFeatures(item: ServiceItem): string[] {
  if (!item.features?.length) return []
  return item.features.filter(feature => Boolean(feature && String(feature).trim()))
}

/**
 * CTAs pointing at the booking page carry the service id so the wizard can
 * preselect it and jump straight past the "choose a service" step.
 */
const { requestBooking } = useBookingRequest()
const bookableServices = ref<BookingServicePublic[]>([])
let bookableReady: Promise<void> | null = null

function normalizeTitle(value?: string): string {
  return (value ?? '').replace(/\u200C/g, '').replace(/\s+/g, ' ').trim().toLowerCase()
}

/**
 * Cards can live on several pages with a different id each, so a card is matched
 * by any of the service refs first and by its title as a last resort.
 */
const bookableIndex = computed(() => {
  const byRef = new Map<string, BookingServicePublic>()
  const byTitle = new Map<string, BookingServicePublic>()
  for (const service of bookableServices.value) {
    for (const ref of service.refs ?? [service.serviceRef]) {
      if (ref) byRef.set(ref, service)
    }
    if (service.id) byRef.set(service.id, service)
    const title = normalizeTitle(service.title)
    if (title && !byTitle.has(title)) byTitle.set(title, service)
  }
  return { byRef, byTitle }
})

function resolveBookable(item: ServiceItem): BookingServicePublic | null {
  const { byRef, byTitle } = bookableIndex.value
  if (item.id) {
    const byIdMatch = byRef.get(item.id)
    if (byIdMatch) return byIdMatch
  }
  return byTitle.get(normalizeTitle(item.title)) ?? null
}

onMounted(() => {
  bookableReady = (async () => {
    try {
      const data = await $fetch<{ items: BookingServicePublic[] }>('/api/booking/services')
      bookableServices.value = data.items
    }
    catch {
      bookableServices.value = []
    }
  })()
  void $fetch<{ items: Array<{
    id: string
    title: string
    description: string
    cover: string | null
    basePrice: number
    sessionCount: number
  }> }>('/api/campaign/packages').then((data) => {
    const fa = pageLocale.value === 'fa'
    packageCards.value = (data.items ?? []).map(row => ({
      id: `pkg:${row.id}`,
      kind: 'package',
      packageId: row.id,
      title: row.title,
      description: row.description,
      image: row.cover || undefined,
      price: formatRialFa(row.basePrice),
      features: [fa ? `پکیج · ${toFaDigits(row.sessionCount)} جلسه` : `Package · ${row.sessionCount} sessions`],
      ctaLabel: fa ? 'خرید پکیج' : 'Buy package',
      ctaIcon: 'i-lucide-layers',
    }))
  }).catch(() => {
    packageCards.value = []
  }).finally(() => {
    packageLoading.value = false
    if (!props.builderPreview) applyPackageQuery()
  })
})

watch(() => route.query.package, applyPackageQuery)

function ctaHandlerFor(item: ServiceItem) {
  if (item.kind === 'page') {
    if (item.requiresAppointment && item.id) {
      return async () => {
        if (bookableReady) await bookableReady
        const service = resolveBookable(item)
        if (service) {
          requestBooking(service)
          return
        }
        const to = item.ctaLink || `/services/${encodeURIComponent(item.pageSlug || item.id || '')}`
        const fallback = props.previewNavigate ?? props.onNavigate
        if (fallback) fallback(to)
        else void navigateTo(to)
      }
    }
    const to = item.ctaLink || (item.pageSlug ? `/services/${encodeURIComponent(item.pageSlug)}` : '/services')
    return () => {
      const fallback = props.previewNavigate ?? props.onNavigate
      if (fallback) fallback(to)
      else void navigateTo(to)
    }
  }
  if (item.kind === 'package' && item.packageId) {
    return () => { selectedPackageId.value = item.packageId ?? null }
  }
  if (!item.id) return undefined
  return async () => {
    if (bookableReady) await bookableReady
    const service = resolveBookable(item)
    if (!service) {
      const fallback = props.previewNavigate ?? props.onNavigate
      if (fallback) fallback(resolveCtaLink(item))
      else await navigateTo(resolveCtaLink(item))
      return
    }
    requestBooking(service)
  }
}

function resolveCtaLink(item: ServiceItem): string {
  const raw = (item.ctaLink || '/booking').trim()
  const serviceRef = resolveBookable(item)?.serviceRef || item.id
  if (!serviceRef) return raw

  const hashIndex = raw.indexOf('#')
  const hash = hashIndex >= 0 ? raw.slice(hashIndex + 1) : ''
  const beforeHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw
  const [path = '/', search = ''] = beforeHash.split('?')
  if ((path.replace(/\/$/, '') || '/') !== '/booking') return raw

  const params = new URLSearchParams(search)
  if (!params.get('service')) params.set('service', serviceRef)
  return `/booking?${params.toString()}#${hash || 'booking-wizard'}`
}

const freeLabel = computed(() => (pageLocale.value === 'fa' ? 'رایگان' : 'Free'))

function resolvePrice(item: ServiceItem): string | undefined {
  if (item.price) return item.price
  const service = resolveBookable(item)
  if (!service) return item.price
  if (service.content?.price) return service.content.price
  return service.paymentMode === 'free' ? freeLabel.value : item.price
}

function cardProps(item: ServiceItem) {
  return {
    title: item.title,
    description: item.description,
    price: resolvePrice(item),
    image: item.image,
    icon: item.icon,
    features: resolveFeatures(item),
    variant: props.cardVariant || 'solid',
    ctaLabel: item.ctaLabel || defaultCta.value,
    ctaLink: resolveCtaLink(item),
    ctaIcon: item.ctaIcon || 'i-lucide-phone',
    detailLink: item.kind === 'package' && item.packageId
      ? `/packages/${encodeURIComponent(item.packageId)}`
      : item.kind === 'page'
        ? item.ctaLink || (item.pageSlug ? `/services/${encodeURIComponent(item.pageSlug)}` : undefined)
        : item.id
          ? `/services/${encodeURIComponent(item.id)}`
          : undefined,
  }
}
</script>

<style scoped>
.block-services__solo {
  width: min(100%, max(18rem, calc((100% - 3rem) / 3)));
}

.block-services-swiper :deep(.swiper-pagination) {
  position: relative !important;
  bottom: auto !important;
  margin-top: 1rem;
  width: 100%;
}

.block-services-swiper :deep(.swiper-pagination-bullet-active) {
  background-color: var(--ui-primary);
}

.block-services__nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border-width: 1px;
  border-style: solid;
  color: var(--ui-primary);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.block-services__nav-btn.swiper-button-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.block-services__nav-btn::after {
  display: none;
}

.block-services__swiper :deep(.block-services__nav-btn.swiper-button-prev),
.block-services__swiper :deep(.block-services__nav-btn.swiper-button-next) {
  position: static;
  inset: auto;
  margin: 0;
}
</style>
